import IntlMessageFormat from 'intl-messageformat';
import SDKMessageBus from '../utility/message-bus';
import { IDict } from '../types/utility';
import { Maybe } from 'maybe-not';
import { BehaviorSubject, Subscription } from 'rxjs';
import defaultTranslation from '../utility/default-strings';

interface ITranslationInfo {
    currentLanguage: string;
    currentTranslations: IDict<any>;
}

export interface ITranslationSetup {
    tFn: (key: string, substitutions?: string[], options?: IDict<any>) => string;
    $locale: BehaviorSubject<string>;
    streamLanguageSubscription: Subscription;
}

export default function t(
    messageBus: SDKMessageBus,
    moduleId: string,
    capabilities: Set<string>,
    legacy?: boolean,
    baseLanguagePath?: string
): Promise<ITranslationSetup> {
    const fetchTranslationsFn = legacy ? _fetchLegacyTranslations : _fetchTranslations;
    const currentLanguagePromise = _getCurrentLanguage(messageBus);

    const translationsPromise = currentLanguagePromise
        .then(langKey => _getCustomLanguageURL(messageBus, moduleId, capabilities, langKey))
        .then(({ langKey, path }) => fetchTranslationsFn(langKey, path, baseLanguagePath))
        .then((translationInfo: ITranslationInfo) => {
            const currentLanguage = translationInfo.currentLanguage;
            const currentTranslations: IDict<any> = {};
            currentTranslations[currentLanguage] =
                translationInfo.currentTranslations[currentLanguage];
            return {
                currentLanguage,
                currentTranslations
            };
        });

    return translationsPromise.then(translationInfo => {
        const $locale = new BehaviorSubject<string>(translationInfo.currentLanguage);
        const streamLanguageSubscription = messageBus
            .onBroadcast<{ langKey: string }>('streamLanguage')
            .subscribe(response => {
                const langKey = response.value.langKey;
                _getCustomLanguageURL(messageBus, moduleId, capabilities, langKey)
                    .then(({ langKey, path }) =>
                        fetchTranslationsFn(langKey, path, baseLanguagePath)
                    )
                    .then((translations: ITranslationInfo) => {
                        translationInfo.currentLanguage = translations.currentLanguage;
                        translationInfo.currentTranslations[translations.currentLanguage] =
                            translations.currentTranslations[translations.currentLanguage];

                        // alert tecton consumers to language change AFTER updates to translationInfo above ^^
                        $locale.next(response.value.langKey);
                    });
            });

        return {
            streamLanguageSubscription,
            $locale,
            tFn: (
                key: string,
                substitutions?: IDict<any> | string[],
                options?: IDict<any>
            ): string => {
                // IntlMessageFormat as of v9.4 has a bug that doesn't allow { in strings
                // this try/catch covers for that case

                let translation;

                try {
                    translation = new IntlMessageFormat(
                        translationInfo.currentTranslations[translationInfo.currentLanguage]?.[
                            key
                        ] ?? key,
                        translationInfo.currentLanguage,
                        options ?? {}
                    ).format(substitutions ?? {});
                } catch (error) {
                    translation = key;
                }
                return translation;
            }
        };
    });
}

type IQ2Window = typeof window & {
    Q2: {
        STRINGS: IDict<any>;
    };
};

function _getCurrentLanguage(messageBus: SDKMessageBus) {
    return messageBus
        .sendRequest<{ langKey: string }>('getCurrentLanguage', Maybe.nothing())
        .then(response => response.value.langKey || 'en-US')
        .catch(() => 'en-US');
}

function _getCustomLanguageURL(
    messageBus: SDKMessageBus,
    moduleId: string,
    capabilities: Set<string>,
    langKey: string
) {
    if (capabilities.has('getCustomLanguageURL')) {
        return messageBus
            .sendRequest<{ path: string }>(
                'getCustomLanguageURL',
                Maybe.just({ moduleId, langKey: langKey.toLowerCase() }).map(JSON.stringify)
            )
            .then(response => ({ path: response.value.path, langKey }));
    }
    return Promise.resolve({ path: '', langKey });
}

function _fetchLegacyTranslations(
    currentLanguage: string = 'en-US',
    customLanguagePath: string = ''
): Promise<ITranslationInfo> {
    const currentTranslations: any = defaultTranslation;
    const hasStrings = !!((window as IQ2Window).Q2 && (window as IQ2Window).Q2.STRINGS);
    const baseStrings = (hasStrings && (window as IQ2Window).Q2.STRINGS[currentLanguage]) || {};
    const customStrings =
        (hasStrings && (window as IQ2Window).Q2.STRINGS[`${currentLanguage}_custom`]) || {};

    currentTranslations[currentLanguage] = {
        ...currentTranslations[currentLanguage],
        ...baseStrings,
        ...customStrings
    };

    return Promise.resolve({
        currentLanguage,
        currentTranslations
    });
}

function _fetchTranslations(
    currentLanguage: string = 'en-US',
    customLanguagePath: string,
    baseLanguagePath: string = '.'
): Promise<ITranslationInfo> {
    const currentTranslations: any = defaultTranslation;
    return fetch(`${baseLanguagePath}/assets/languages/${currentLanguage}.json`)
        .then(response => {
            return response;
        })
        .then(response => response.json())
        .then((moduleBaseTranslation: IDict<any>) => {
            currentTranslations[currentLanguage] = {
                ...currentTranslations[currentLanguage],
                ...moduleBaseTranslation
            };
        })
        .then(() => _fetchCustomLanguage(customLanguagePath))
        .then(response => response.json())
        .then((moduleCustomizedTranslation: IDict<any>) => {
            currentTranslations[currentLanguage] = {
                ...currentTranslations[currentLanguage],
                ...moduleCustomizedTranslation
            };
            return {
                currentLanguage,
                currentTranslations
            };
        })
        .catch(() => {
            return {
                currentLanguage,
                currentTranslations
            };
        });
}

function _fetchCustomLanguage(customLanguagePath: string) {
    if (customLanguagePath === '') {
        return Promise.reject('no custom language string support.');
    }
    return fetch(customLanguagePath);
}
